import type { Category, CategoryDraft, Product } from './catalog.types';
import { resolveImageUrl } from '../../../utils/image';

type CategoryListProps = {
  categories: Category[];
  products: Product[];
  compactView: boolean;
  canDelete: boolean;
  onEdit: (category: CategoryDraft) => void;
  onDelete: (id: number) => void;
};

export function CategoriesList({ categories, products, compactView, canDelete, onEdit, onDelete }: CategoryListProps) {
  if (compactView) {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {categories.map((c) => (
          <div key={c.id} className="admin-card">
            <div style={{ fontWeight: 700 }}>{c.name}</div>
            <div>ID: {c.id}</div>
            <div>Статус: {c.is_active ? 'Активна' : 'Отключена'}</div>
            <div className="admin-cell-actions">
              <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => onEdit({ ...c, product_ids: products.filter((p) => p.category_id === c.id).map((p) => p.id) })}>
                Ред.
              </button>
              <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canDelete} onClick={() => onDelete(c.id)} style={{ color: 'red' }}>
                Удал.
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th className="admin-th admin-num">ID</th>
            <th className="admin-th">Название</th>
            <th className="admin-th admin-center">Статус</th>
            <th className="admin-th admin-center">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr className="admin-table-row">
            <td className="admin-td admin-num admin-mono">0</td>
            <td className="admin-td">Общая (все товары в наличии)</td>
            <td className="admin-td admin-center">✅</td>
            <td className="admin-td admin-center">Авто</td>
          </tr>
          {categories.map((c) => (
            <tr key={c.id} className="admin-table-row">
              <td className="admin-td admin-num admin-mono">{c.id}</td>
              <td className="admin-td">{c.name}</td>
              <td className="admin-td admin-center">{c.is_active ? '✅' : '❌'}</td>
              <td className="admin-td admin-center">
                <div className="admin-cell-actions admin-cell-actions-center">
                  <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => onEdit({ ...c, product_ids: products.filter((p) => p.category_id === c.id).map((p) => p.id) })}>
                    Ред.
                  </button>
                  <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canDelete} onClick={() => onDelete(c.id)} style={{ color: 'red' }}>
                    Удал.
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type ProductListProps = {
  products: Product[];
  compactView: boolean;
  loading: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  isStatusUpdating: (productId: number) => boolean;
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
  onToggleActive: (product: Product, nextValue: boolean) => void;
  onToggleInStock: (product: Product, nextValue: boolean) => void;
};

export function ProductsList({
  products,
  compactView,
  loading,
  canUpdate,
  canDelete,
  isStatusUpdating,
  onEdit,
  onDelete,
  onToggleActive,
  onToggleInStock,
}: ProductListProps) {
  if (loading) return <p>Загрузка...</p>;
  if (compactView) {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {products.map((p) => (
          <div key={p.id} className="admin-card">
            <div className="admin-product-card">
              <div className="admin-product-thumb">
                {p.images?.[0] ? (
                  <img src={resolveImageUrl(p.images[0])} alt="" loading="lazy" />
                ) : (
                  <div className="admin-thumb-placeholder admin-thumb-placeholder-lg" aria-hidden="true">
                    Нет фото
                  </div>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{p.name}</div>
                <div>Категория: {p.category_name || '-'}</div>
                <div>Цена: {p.price} ₽</div>
                <div>Себестоимость: {p.cost_price != null ? `${p.cost_price} ₽` : '—'}</div>
                <div>Статус: {p.is_active ? 'Активен' : 'Выключен'}, {p.in_stock ? 'В наличии' : 'Нет в наличии'}</div>
              </div>
            </div>
            <div className="admin-cell-actions">
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={Boolean(p.is_active)}
                  disabled={!canUpdate || isStatusUpdating(p.id)}
                  onChange={(e) => onToggleActive(p, e.target.checked)}
                />
                {isStatusUpdating(p.id) ? 'Сохр...' : 'Активен'}
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={Boolean(p.in_stock)}
                  disabled={!canUpdate || isStatusUpdating(p.id)}
                  onChange={(e) => onToggleInStock(p, e.target.checked)}
                />
                В наличии
              </label>
              <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canUpdate} onClick={() => onEdit(p)}>Ред.</button>
              <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canDelete} onClick={() => onDelete(p.id)} style={{ color: 'red' }}>Удал.</button>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th className="admin-th admin-num">ID</th>
            <th className="admin-th admin-center">Фото</th>
            <th className="admin-th">Название</th>
            <th className="admin-th">Категория</th>
            <th className="admin-th">Состав</th>
            <th className="admin-th admin-num">Цена</th>
            <th className="admin-th admin-num">Себестоимость</th>
            <th className="admin-th admin-center">Статус</th>
            <th className="admin-th admin-center">Действия</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr key={p.id} className="admin-table-row">
              <td className="admin-td admin-num admin-mono">{p.id}</td>
              <td className="admin-td admin-center admin-cell-photo">
                {p.images?.[0] ? (
                  <img src={resolveImageUrl(p.images[0])} alt="" className="admin-thumb" loading="lazy" />
                ) : (
                  <div className="admin-thumb admin-thumb-placeholder" aria-hidden="true">
                    Нет
                  </div>
                )}
              </td>
              <td className="admin-td">{p.name}</td>
              <td className="admin-td">{p.category_name || '-'}</td>
              <td className="admin-td admin-cell-composition">{Array.isArray(p.composition) ? p.composition.map((line) => `${line.name} - ${line.quantity}`).join(', ') : '-'}</td>
              <td className="admin-td admin-num admin-mono">{p.price} ₽</td>
              <td className="admin-td admin-num admin-mono">{p.cost_price != null ? `${p.cost_price} ₽` : '—'}</td>
              <td className="admin-td admin-center admin-cell-status">
                <div className="admin-cell-actions admin-cell-actions-center">
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(p.is_active)}
                      disabled={!canUpdate || isStatusUpdating(p.id)}
                      onChange={(e) => onToggleActive(p, e.target.checked)}
                    />
                    {isStatusUpdating(p.id) ? 'Сохр...' : 'Активен'}
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(p.in_stock)}
                      disabled={!canUpdate || isStatusUpdating(p.id)}
                      onChange={(e) => onToggleInStock(p, e.target.checked)}
                    />
                    В наличии
                  </label>
                  {p.is_hit ? '🔥' : ''}
                </div>
              </td>
              <td className="admin-td admin-center">
                <div className="admin-cell-actions admin-cell-actions-center">
                  <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canUpdate} onClick={() => onEdit(p)}>Ред.</button>
                  <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={!canDelete} onClick={() => onDelete(p.id)} style={{ color: 'red' }}>Удал.</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

